// i18next backend that loads translations as bundles instead of one request per namespace (#3929).
//
// The server (#3928) compiles, per language and audience (`customer`, `admin`), ONE bundle with every
// namespace of that audience, English merged key by key under the language's own strings, and names it
// by its fingerprint. The boot therefore costs three requests per language: the manifest (always
// revalidated) and two bundles (immutable: the browser keeps them for a year and a changed catalogue
// is a new URL).
//
// There is NO fallback language in the browser. A language without a catalogue of its own still gets a
// complete bundle (its missing keys are the English ones, filled in by the compiler), so asking i18next
// for `en` as well would only add requests for strings the bundle already carries.

import { I18N_ROUTES } from '@/shared/constants/config-routes'
import { API_BASE_URL } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

const BUNDLE_AUDIENCES = ['customer', 'admin'] as const
type BundleAudience = (typeof BUNDLE_AUDIENCES)[number]

/** Namespaces served in the admin bundle; every other namespace is in the customer one. */
export const ADMIN_BUNDLE_NAMESPACES = ['admin'] as const

export const resolveBundleAudience = (namespace: string): BundleAudience =>
  (ADMIN_BUNDLE_NAMESPACES as readonly string[]).includes(namespace) ? 'admin' : 'customer'

/**
 * Per-request network budget. `fetch` never times out on its own: a backend that accepts the connection
 * and then stalls would hold the namespace "pending" and, with it, the first paint.
 */
const REQUEST_TIMEOUT_MS = 8000

/** `AbortSignal.timeout` guarded for environments that do not expose it. */
export const createRequestTimeoutSignal = (timeoutMs: number): AbortSignal | undefined =>
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(timeoutMs)
    : undefined

type Namespaces = Record<string, Record<string, unknown>>

/** An answer that arrived but is not what the server contract promises (not a bundle, no such namespace). */
class BundleFormatError extends Error {
  constructor(
    message: string,
    readonly path: string | null = null,
  ) {
    super(message)
    this.name = 'BundleFormatError'
  }
}

/**
 * A 200 whose body is not JSON: a captive portal, a proxy error page, an `<html>` from a gateway that is
 * restarting. It is a transport problem (the server never spoke), so it heals by asking again.
 */
export class BundleBodyUnreadableError extends Error {
  constructor(readonly path: string) {
    super(`${path} did not answer valid JSON`)
    this.name = 'BundleBodyUnreadableError'
  }
}

/** The request never produced an answer (offline, DNS, connection cut, timeout), or the body was cut mid-read. */
class BundleNetworkError extends Error {
  readonly timedOut: boolean

  constructor(
    readonly path: string,
    cause: unknown,
  ) {
    // Read `name` off the value itself: a DOMException is not always an `Error` (jsdom, older engines).
    const name = isRecord(cause) && typeof cause.name === 'string' ? cause.name : 'Error'
    const detail =
      isRecord(cause) && typeof cause.message === 'string' ? cause.message : String(cause)
    const timedOut = name === 'TimeoutError' || name === 'AbortError'
    super(timedOut ? `${name}: ${detail}` : detail)
    this.name = 'BundleNetworkError'
    this.timedOut = timedOut
  }
}

/** An HTTP answer that is not a success; the status tells a stale fingerprint (404) from the rest. */
class BundleRequestError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
  ) {
    super(`${path} returned ${status}`)
    this.name = 'BundleRequestError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

const textEncoder = new TextEncoder()

/** The parsed body and its exact size on the wire (UTF-8 bytes of the text). */
interface FetchedJson {
  body: unknown
  bytes: number
}

const fetchJson = async (path: string): Promise<FetchedJson> => {
  let text: string
  try {
    const response = await fetch(API_BASE_URL + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: createRequestTimeoutSignal(REQUEST_TIMEOUT_MS),
    })
    if (!response.ok) throw new BundleRequestError(path, response.status)
    text = await response.text()
  } catch (error) {
    if (error instanceof BundleRequestError) throw error
    // A rejection of `fetch` or while the body is READ (a timeout or a connection cut mid-download) is a
    // network failure like any other: it heals by asking again.
    throw new BundleNetworkError(path, error)
  }
  try {
    return { body: JSON.parse(text) as unknown, bytes: textEncoder.encode(text).byteLength }
  } catch {
    throw new BundleBodyUnreadableError(path)
  }
}

/** The fingerprint of one audience's bundle, read from `{ success, data: { bundles } }`. */
const readFingerprint = (body: unknown, audience: BundleAudience, path: string): string => {
  const bundles = isRecord(body) && isRecord(body.data) ? body.data.bundles : undefined
  const entry = isRecord(bundles) ? bundles[audience] : undefined
  if (!isRecord(entry) || typeof entry.hash !== 'string' || entry.hash.length === 0) {
    throw new BundleFormatError(`${path} has no ${audience} bundle`, path)
  }
  return entry.hash
}

/**
 * The manifest, checked INSIDE the remembered request: both audiences must name a fingerprint. A 200 that
 * is not a usable manifest rejects the promise, so it is forgotten like any failure and the next read asks
 * again (it would otherwise be remembered, fulfilled, until the page is reloaded).
 */
const assertManifest = (body: unknown, path: string): unknown => {
  for (const audience of BUNDLE_AUDIENCES) readFingerprint(body, audience, path)
  return body
}

/** The namespaces of a bundle, read from its raw body `{ locale, audience, namespaces }`. */
const readNamespaces = (body: unknown, path: string): Namespaces => {
  if (!isRecord(body) || !isRecord(body.namespaces)) {
    throw new BundleFormatError(`${path} is not a translation bundle`, path)
  }
  return body.namespaces as Namespaces
}

/**
 * Network failures, timeouts, 5xx and 429 may heal on their own: i18next retries a read whose callback
 * carries a truthy second argument (`maxRetries` / `retryTimeout` of `config.ts`), and the background
 * retries of `failure-resilience.ts` start from the same failure. A 4xx, or an answer that is not what
 * the contract promises, will not change by asking again.
 *
 * The one 4xx that DOES heal is the BUNDLE's 404: the engine already asked for the manifest again once,
 * so the server is still naming a fingerprint it no longer serves (the catalogue is being published, or
 * another process still has the old one). A 404 of the MANIFEST is a missing route and stays final.
 *
 * A 200 whose body is not JSON (an `<html>` page from a gateway or a portal) is transient too: it is the
 * network answering in place of the server, not the server breaking its contract.
 */
export const isTransientBundleFailure = (error: Error): boolean => {
  if (error instanceof BundleFormatError) return false
  if (error instanceof BundleBodyUnreadableError) return true
  if (error instanceof BundleRequestError) {
    if (error.status === 404) return !error.path.startsWith(I18N_ROUTES.MANIFEST(''))
    return error.status >= 500 || error.status === 429
  }
  return true
}

export type BundleFailureKind = 'network' | 'timeout' | 'not-json' | 'contract' | 'http'

/** What a log line needs to tell the failures apart: the kind, the route, the HTTP status and the message. */
export const describeBundleFailure = (
  error: unknown,
): { kind: BundleFailureKind; path: string | null; status: number | null; message: string } => {
  const failure = toError(error)
  if (failure instanceof BundleRequestError) {
    return { kind: 'http', path: failure.path, status: failure.status, message: failure.message }
  }
  if (failure instanceof BundleNetworkError) {
    return {
      kind: failure.timedOut ? 'timeout' : 'network',
      path: failure.path,
      status: null,
      message: failure.message,
    }
  }
  if (failure instanceof BundleBodyUnreadableError) {
    return { kind: 'not-json', path: failure.path, status: null, message: failure.message }
  }
  if (failure instanceof BundleFormatError) {
    return { kind: 'contract', path: failure.path, status: null, message: failure.message }
  }
  return { kind: 'network', path: null, status: null, message: failure.message }
}

type ReadCallback = (error: Error | null, data: Record<string, unknown> | boolean | null) => void

export class TranslationBundleBackend {
  static type = 'backend' as const

  // One memory per i18n instance. What is remembered is the PROMISE, so the namespaces that i18next
  // asks for together (and the second read a StrictMode mount makes) share a single request.
  private readonly bundleByKey = new Map<string, Promise<Namespaces>>()
  private readonly manifestByLanguage = new Map<string, Promise<unknown>>()

  // i18next calls it with its services and options; this backend needs neither.
  init(): void {}

  read(language: string, namespace: string, callback: ReadCallback): void {
    this.loadNamespace(language, namespace).then(
      (data) => callback(null, data),
      (error: unknown) => {
        const failure = toError(error)
        callback(failure, isTransientBundleFailure(failure) ? true : null)
      },
    )
  }

  private async loadNamespace(
    language: string,
    namespace: string,
  ): Promise<Record<string, unknown>> {
    const audience = resolveBundleAudience(namespace)
    const catalogue = (await this.loadBundle(language, audience))[namespace]
    if (!isRecord(catalogue)) {
      // Not an empty namespace: i18next would take `{}` as loaded and the whole screen would show
      // humanised keys with no error anywhere. It means the audience split of this file and the one of
      // the server (`bundleAudiencesMatchServer.test.ts`) have drifted apart.
      logger.error('[i18n] The bundle does not carry the namespace', {
        language,
        audience,
        namespace,
      })
      // The server may have published a complete bundle since: forget the remembered one (and the manifest
      // that named it) so a later read, the manual retry of the notice among them, asks again (#4114).
      this.bundleByKey.delete(`${language}:${audience}`)
      this.manifestByLanguage.delete(language)
      throw new BundleFormatError(
        `The ${audience} bundle of "${language}" has no namespace "${namespace}"`,
      )
    }
    return catalogue
  }

  private loadBundle(language: string, audience: BundleAudience): Promise<Namespaces> {
    // The retry lives INSIDE the remembered request: the namespaces that arrive together share it, so a
    // stale fingerprint costs one new manifest and not one per namespace.
    return this.remember(this.bundleByKey, `${language}:${audience}`, async () => {
      const attempt = async (retry: boolean): Promise<Namespaces> => {
        const manifestPath = I18N_ROUTES.MANIFEST(language)
        const manifestRequest = this.remember(this.manifestByLanguage, language, async () =>
          assertManifest((await fetchJson(manifestPath)).body, manifestPath),
        )
        try {
          const hash = readFingerprint(await manifestRequest, audience, manifestPath)
          const bundlePath = I18N_ROUTES.BUNDLE(language, audience, hash)
          const { body, bytes } = await fetchJson(bundlePath)
          const namespaces = readNamespaces(body, bundlePath)
          logger.debug('[i18n] Translation bundle loaded', { language, audience, hash, bytes })
          return namespaces
        } catch (error) {
          // The fingerprint was current when the manifest was read and the catalogue changed since: the
          // server only keeps the current bundle. Ask for the manifest again, once.
          // Only the BUNDLE's 404 means that: a 404 of the manifest is a missing route, not a stale fingerprint.
          if (
            retry ||
            !(error instanceof BundleRequestError) ||
            error.status !== 404 ||
            error.path === manifestPath
          ) {
            throw error
          }
          logger.warn('[i18n] Bundle is no longer current; requesting the manifest again', {
            language,
            audience,
          })
          // Only if it is still the manifest that named the stale fingerprint: another audience may
          // already have replaced it with a fresh one.
          if (this.manifestByLanguage.get(language) === manifestRequest) {
            this.manifestByLanguage.delete(language)
          }
          return attempt(true)
        }
      }
      return attempt(false)
    })
  }

  /**
   * Remember a request by key. A failure is NOT remembered: the entry is dropped (unless a newer one
   * already replaced it) so the next read asks again instead of serving the same error forever.
   */
  private remember<T>(
    memory: Map<string, Promise<T>>,
    key: string,
    load: () => Promise<T>,
  ): Promise<T> {
    const existing = memory.get(key)
    if (existing) return existing
    const pending: Promise<T> = load().catch((error: unknown) => {
      if (memory.get(key) === pending) memory.delete(key)
      throw error
    })
    memory.set(key, pending)
    return pending
  }
}
